/**
 * Simple Product Review Dialog for regular products (non-ROD).
 * Uses shared BaseModal and review components for consistency with campaigns.
 */
import { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';

import { api, useAPIContext } from '../../api/context';
import toast from '../../utils/toast';
import review from '../../components/review';
import BaseModal from '../BaseModal';
import styles from './ProductReview.module.scss';

const ProductReview = ({ item, onClose, onSubmit }) => {
  const { token } = useAPIContext();
  const product = item.product;

  const [submitLoading, setSubmitLoading] = useState(false);

  const submit = async (values) => {
    setSubmitLoading(true);

    try {
      const response = await api(token).v1.products.review.post(
        { product_id: product.id },
        {
          rating: values.stars,
          title: values.title || '',
          text: values.text,
        }
      );

      setSubmitLoading(false);

      if (response.status === 201) {
        toast.show(toast.KIND.SUCCESS, 'Review submitted!');
        if (onSubmit) onSubmit(response.body);
        if (onClose) onClose();
      } else if (response.status === 400) {
        toast.show(toast.KIND.ERROR, response.body?.error || response.body);
      }
    } catch (err) {
      setSubmitLoading(false);
      toast.show(toast.KIND.WARNING, err.message || err);
    }
  };

  const loading = submitLoading;

  return (
    <BaseModal title="Review" onClose={onClose}>
      <div className={styles.root}>
        {/* Product header */}
        <div className={styles.product}>
          {product.images?.[0] && (
            <img src={product.images[0].image} alt="" />
          )}
          <div>
            <span>{product.brand_name}</span>
            <strong>{product.name}</strong>
          </div>
        </div>

        <Formik
          initialValues={{
            stars: 0,
            title: '',
            text: '',
          }}
          validationSchema={yup.object({
            stars: yup.number()
              .min(1, 'Rating is required')
              .max(5, 'Invalid rating'),
            title: yup.string(),
            text: yup.string()
              .required('Review text is required'),
          })}
          validateOnChange={false}
          onSubmit={(values) => {
            if (loading) return;
            submit(values);
          }}
        >
          <Form>
            <review.Stars
              className={styles.stars}
              loading={loading}
              count={5}
            />

            <div className={styles.field}>
              <label htmlFor='title'>Title (optional)</label>
              <Field
                id='title'
                aria-label='title'
                name='title'
                type='text'
                disabled={loading}
              />
            </div>

            <review.Text
              className={styles.text}
              loading={loading}
              label="Review"
              prefix=""
              length={{
                min: 0,
                max: 0,
              }}
            />

            <button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Review'}
            </button>
          </Form>
        </Formik>
      </div>
    </BaseModal>
  );
};

export default ProductReview;
